import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'

/** Shared so the column and the space the app shell keeps for it are always the same width. */
export const RANGER_PANEL_WIDTH_CLASS = 'w-[340px] 2xl:w-[380px]'

interface RangerPanelContextValue {
  /** True only on pages with the app shell. Other pages have no nav and no room kept for the column. */
  isOpen: boolean
  registerAppShell: () => () => void
  openRangerPanel: () => void
  closeRangerPanel: () => void
  toggleRangerPanel: () => void
}

const RangerPanelContext = createContext<RangerPanelContextValue | null>(null)

export function RangerPanelProvider({ children }: { children: ReactNode }) {
  const [wantsOpen, setWantsOpen] = useState(false)
  const [appShellCount, setAppShellCount] = useState(0)

  const registerAppShell = useCallback(() => {
    setAppShellCount((count) => count + 1)
    return () => setAppShellCount((count) => count - 1)
  }, [])
  const openRangerPanel = useCallback(() => setWantsOpen(true), [])
  const closeRangerPanel = useCallback(() => setWantsOpen(false), [])
  const toggleRangerPanel = useCallback(() => setWantsOpen((prev) => !prev), [])

  const value: RangerPanelContextValue = {
    isOpen: wantsOpen && appShellCount > 0,
    registerAppShell,
    openRangerPanel,
    closeRangerPanel,
    toggleRangerPanel,
  }

  return <RangerPanelContext.Provider value={value}>{children}</RangerPanelContext.Provider>
}

export function useRangerPanel(): RangerPanelContextValue {
  const context = useContext(RangerPanelContext)
  if (!context) {
    throw new Error('useRangerPanel must be used within RangerPanelProvider')
  }
  return context
}

/** Marks the current page as one that has the nav and keeps room for the Ranger column. */
export function useRegisterRangerAppShell(): void {
  const { registerAppShell } = useRangerPanel()
  useEffect(() => registerAppShell(), [registerAppShell])
}
